import { Info, Wallet } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLang, useSiteSettings } from "@/context/LanguageContext";
import { trs } from "@/lib/translations";

export const depositOf = (total, quote, settings) => {
  const extra = quote ? quote.extra_costs : Number(settings?.extra_costs) || 0;
  const pct = quote?.deposit_percent ?? settings?.deposit_percent ?? 0;
  return Math.floor((total - extra) * pct / 100 + extra + 0.5);
};

/** Preiszeilen (Nächte, Rabatt, Endreinigung, Servicegebühr, Extras, Gutschein, Gesamt) + optional Zahlungshinweis – Objektseite und Buchungsseite. */
export const PriceBreakdown = ({ prop, quote, nights, total, paymentHint = true, big = false }) => {
  const { lang, t } = useLang();
  const settings = useSiteSettings();
  const extraCosts = quote ? quote.extra_costs : Number(settings?.extra_costs) || 0;
  return (
    <div className="space-y-2 text-sm" data-testid="price-breakdown">
      {quote ? quote.breakdown.map((b, i) => (
        <div key={i} className="flex justify-between text-stone-600" data-testid="price-breakdown-row"><span>{b.nights} × €{b.price_per_night}</span><span>€{b.price_per_night * b.nights}</span></div>
      )) : <div className="flex justify-between text-stone-600"><span>{nights} × €{prop.price_per_night}</span><span>€{nights * prop.price_per_night}</span></div>}
      {quote?.discount > 0 && (
        <div className="flex justify-between text-red-600 font-medium" data-testid="price-discount-row">
          <span>{quote.offer ? `${t.offers.discount}: ${quote.offer.name?.[lang] || quote.offer.name?.de}` : `${t.property.lastMinute} −${quote.discount_percent} %`}</span><span>−€{quote.discount}</span>
        </div>
      )}
      <div className="flex justify-between text-stone-600" data-testid="price-cleaning-row">
        <span>{t.property.cleaning}</span>
        {quote?.cleaning_waived ? <span className="text-brand text-xs font-medium" data-testid="price-cleaning-waived">{t.prices.cleaningWaived.replace("{n}", quote.cleaning_waived_from)}</span> : <span>€{quote ? quote.cleaning_fee : prop.cleaning_fee}</span>}
      </div>
      {extraCosts > 0 && (
        <div className="flex justify-between text-stone-600" data-testid="price-extra-costs-row">
          <span className="inline-flex items-center gap-1">{t.property.extraCosts}
            {trs(settings, "extra_costs_info", lang) && (
              <TooltipProvider delayDuration={100}><Tooltip>
                <TooltipTrigger asChild><button type="button" className="text-stone-400 hover:text-brand" aria-label="Info" data-testid="extra-costs-info"><Info className="w-3.5 h-3.5" /></button></TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed" data-testid="extra-costs-info-text">{trs(settings, "extra_costs_info", lang)}</TooltipContent>
              </Tooltip></TooltipProvider>
            )}
          </span><span>€{extraCosts}</span>
        </div>
      )}
      {(quote?.extras || []).map((e) => (
        <div key={e.key} className="flex justify-between text-stone-600" data-testid={`price-extra-row-${e.key}`}>
          <span>{t.extras[e.key]}{e.qty > 1 ? ` ×${e.qty}` : ""} · €{e.price} {e.unit === "stay" ? t.extras.perStay : t.extras.perNight}</span><span>€{e.amount}</span>
        </div>
      ))}
      {quote?.voucher && !quote.voucher.error && (
        <div className="flex justify-between text-brand font-medium" data-testid="price-voucher-row"><span>{t.booking.voucherRow.replace("{code}", quote.voucher.code)}</span><span>−€{quote.voucher.amount}</span></div>
      )}
      <div className={`flex justify-between font-semibold text-stone-900 pt-3 border-t border-stone-200 ${big ? "text-lg" : ""}`}>
        <span>{big ? t.booking.totalPrice : t.property.total}</span><span data-testid="booking-total" className={big ? "text-brand" : ""}>€{total}</span>
      </div>
      {paymentHint && settings && (
        <div className="flex gap-2 items-start text-xs text-stone-600 bg-soft rounded-lg p-3" data-testid="payment-hint">
          <Wallet className="w-4 h-4 text-brand shrink-0 mt-0.5" />
          {prop.payment_mode === "landlord" ? <span>{t.property.payLandlord}</span>
            : <span>{t.property.payDeposit.replace("{pct}", quote?.deposit_percent ?? settings.deposit_percent)}{nights > 0 && ` (€${depositOf(total, quote, settings)})`}. {t.property.payRest}</span>}
        </div>
      )}
    </div>
  );
};
